import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'ha/i18n';

import { AppBar, Button, Divider, Grid, Toolbar, units } from '@ha/dsl';
import LanguageSwitcher from 'ha/modules/LanguageSwitcher';
import Track from 'ha/components/track/SmartTrack';

import { SearchBar } from 'ha/modules/SearchBar';
import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { LOGIN, REGISTER } from 'ha/constants/pageNames';

import NavBarLogo from './NavBarLogo';
import UnauthenticatedNavigation from './UnauthenticatedNavigation';
import AuthenticatedNavigation from '../containers/AuthenticatedNavigation.lazy';
import PricingLink from './PricingLink';
import DropdownMenu from './DropdownMenu';
import useListYourPlaceCTA from '../hooks/useListYourPlaceCTA';

import { getLearnMenuLinks, getProductsMenuLinks } from '../utils';

const useStyles = makeStyles(theme => ({
  // fixed width to allow centering via space-between
  fixedWidth: {
    [theme.breakpoints.up('md')]: {
      minWidth: units.rem(16),
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: units.rem(23),
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: '33%',
    },
  },
  darkBlue: {
    color: theme.palette.grey[700],
  },
  searchBar: {
    marginLeft: units.rem(1),
    width: units.rem(20),
  },
  userInfo: {
    marginLeft: 'auto',
  },
  links: {
    marginLeft: 'auto',
  },
  divider: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
}));

interface Props {
  hasStickyNav?: boolean;
  showMiddleMenu?: boolean;
  isAuthenticated: boolean;
  showSearchBar?: boolean;
}

export const NavBarDesktop: React.FC<Props> = ({
  hasStickyNav,
  showMiddleMenu = true,
  isAuthenticated,
  showSearchBar,
}) => {
  const intl = useIntl();
  const { T } = intl;
  const classes = useStyles();
  const ctaProps = useListYourPlaceCTA();
  const { name } = usePage();
  const isAuthPage = name === LOGIN || name === REGISTER;

  return (
    <AppBar
      position={hasStickyNav ? 'sticky' : 'relative'}
      color="inherit"
      elevation={0}
      data-test-locator="NavBar Top Desktop"
    >
      <Toolbar>
        <Grid spacing={1} alignItems="center" justify="space-between" container>
          <Grid item className={classes.fixedWidth}>
            <Grid container>
              <NavBarLogo showOnlyIcon={showSearchBar} />
              {showSearchBar && (
                <div className={classes.searchBar}>
                  <SearchBar />
                </div>
              )}
            </Grid>
          </Grid>

          {showMiddleMenu && (
            <Grid
              item
              className={classes.links}
              data-test-locator="Navbar Desktop MiddleMenu"
            >
              <div className={classes.darkBlue}>
                <DropdownMenu
                  id="navbar_desktop_products"
                  title={T('navbar_products_button')}
                  data={getProductsMenuLinks(intl)}
                  testId="Products NavBar"
                />
                <PricingLink />
                <DropdownMenu
                  id="navbar_desktop_learn_menu"
                  title={T('navbar_learn_button')}
                  data={getLearnMenuLinks(intl)}
                  testId="Learn NavBar"
                />
              </div>
            </Grid>
          )}

          <Grid item className={cx(classes.fixedWidth, classes.userInfo)}>
            <Grid container justify="flex-end" alignItems="center" spacing={2}>
              {!isAuthenticated && !isAuthPage && (
                <Grid item>
                  <UnauthenticatedNavigation />
                </Grid>
              )}
              {ctaProps.isVisible && (
                <Grid item>
                  <Track name="Clicked List your place in header" type="button">
                    <Button
                      variant="outlined"
                      color="primary"
                      data-test-locator="List Your Place CTA"
                      href={ctaProps.href}
                      rel={ctaProps.rel}
                    >
                      {ctaProps.label}
                    </Button>
                  </Track>
                </Grid>
              )}
              {isAuthenticated && (
                <Grid item>
                  <AuthenticatedNavigation />
                </Grid>
              )}
              <Grid item>
                <LanguageSwitcher className={classes.darkBlue} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider className={classes.divider} />
    </AppBar>
  );
};

export default NavBarDesktop;
